import {tt} from '/provider/TranslationProvider/TranslationProvider.util';

import {
  ABOUT_MAX_LENGTH,
  BLOG_DOMAIN_REGEX,
  EMAIL_REGEX,
  normalizeCountry,
  SLUG_REGEX,
  ZIP_REGEX,
} from '../ManageMySite.constants';
import {FormErrors} from '../ManageMySite.types';

const t = (key: string, fallback: string) => tt(key, fallback) as string;

export const splitDisplayName = (value: string) => {
  const trimmed = (value || '').trim();

  if (!trimmed) {
    return {firstName: '', lastName: ''};
  }

  const parts = trimmed.split(/\s+/);

  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' ') || '',
  };
};

export const stripPhoneToDigits = (value: string) =>
  String(value || '').replace(/\D/g, '');

export const isValidUSPhone = (value: string) => {
  const digits = stripPhoneToDigits(value);
  if (digits.length === 11 && digits.startsWith('1')) {
    return true;
  }
  return digits.length === 10;
};

export const getUSPhoneValidationMessage = (value: string): string => {
  if (!value) {
    return '';
  }

  return isValidUSPhone(value)
    ? ''
    : t('errPhoneInv', 'Please enter a valid 10-digit US phone number.');
};

export const formatUSPhone = (value: string) => {
  let digits = stripPhoneToDigits(value);

  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }

  const len = digits.length;
  if (len === 0) return '';
  if (len < 4) return digits;
  if (len < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

export const formatDisplayPhone = (phone: string, isUS: boolean): string => {
  if (!phone) return '';
  if (isUS) {
    // use non-breaking space after +1
    return `+1\u00A0${formatUSPhone(phone)}`;
  }
  return phone;
};

export const validateManageMySiteForm = ({
  slug,
  name,
  mail,
  primary,
  secondary,
  zip,
  aboutVal,
  countryVal,
}: {
  slug: string;
  name: string;
  mail: string;
  primary: string;
  secondary: string;
  zip: string;
  aboutVal: string;
  countryVal: string;
}): FormErrors => {
  const errors: FormErrors = {};

  const trimmedName = (name || '').trim();
  const trimmedSlug = (slug || '').trim().toLowerCase();
  const trimmedMail = (mail || '').trim();
  const trimmedPrimary = (primary || '').trim();
  const trimmedSecondary = (secondary || '').trim();
  const trimmedZip = (zip || '').trim();

  if (!trimmedName) {
    errors.name = t('errNameReq', 'Enter your full name (e.g., John Doe)');
  } else if (trimmedName.split(/\s+/).length < 2) {
    errors.name = t(
      'errNameFormat',
      'Enter first and last name (e.g., John Doe)',
    );
  }

  const normalizedSlug = trimmedSlug.toLowerCase();

  if (!trimmedSlug) {
    errors.slug = t('errSlugReq', 'Web Alias is required.');
  } else if (normalizedSlug === 'coach') {
    errors.slug = t(
      'errSlugReserved',
      'This web alias is not allowed. Please choose a different one.',
    );
  } else if (!SLUG_REGEX.test(trimmedSlug)) {
    errors.slug = t(
      'errSlugFormat',
      'Use only letters and numbers. No special characters allowed.',
    );
  }
  if (trimmedMail && !EMAIL_REGEX.test(trimmedMail)) {
    errors.mail = t('errEmailInv', 'Please enter a valid email address.');
  }

  const effectiveCountry = normalizeCountry(countryVal) || 'US';

  if (
    trimmedPrimary &&
    effectiveCountry === 'US' &&
    !isValidUSPhone(trimmedPrimary)
  ) {
    errors.primary = t(
      'errPhoneInv',
      'Please enter a valid 10-digit US phone number.',
    );
  }

  if (
    trimmedSecondary &&
    effectiveCountry === 'US' &&
    !isValidUSPhone(trimmedSecondary)
  ) {
    errors.secondary = t(
      'errPhoneInv',
      'Please enter a valid 10-digit US phone number.',
    );
  }

  if (trimmedZip && effectiveCountry === 'US' && !ZIP_REGEX.test(trimmedZip)) {
    errors.zip = t(
      'errZipInv',
      'Please enter a valid US ZIP code (e.g., 12345).',
    );
  }

  if (aboutVal && aboutVal.length > ABOUT_MAX_LENGTH) {
    errors.about = t(
      'errAboutMax',
      `Please remove ${aboutVal.length - ABOUT_MAX_LENGTH} characters.`,
    );
  }

  return errors;
};

export const validateSocialLinks = (
  links: {socialNetworkID: number; type: string; url: string}[],
): Record<number, string> => {
  const errors: Record<number, string> = {};

  links.forEach(link => {
    const value = link.url.trim();
    const isBlog = link.type?.toLowerCase().includes('blog');

    if (!value) {
      return;
    }

    if (isBlog) {
      if (!BLOG_DOMAIN_REGEX.test(value)) {
        errors[link.socialNetworkID] = t(
          'errBlogDomain',
          'Please add a domain ending, like .com or .org.',
        );
      }
      return;
    }
  });

  return errors;
};
